<script lang="ts" setup>
import { cva, type VariantProps } from 'class-variance-authority'
import { X } from 'lucide-vue-next'
import type { HTMLAttributes } from 'vue'
import { computed } from 'vue'

import { AIndicator, cn } from '../../../../'

const badgeVariants = cva('atabix-badge', {
  variants: {
    variant: {
      default: 'atabix-badge--outlined',
      filled: 'atabix-badge--filled',
      destructive: 'atabix-badge--destructive',
    },
  },
})

export type BadgeVariant = VariantProps<typeof badgeVariants>

// Props
export interface ABadgeProperties {
  label?: string
  class?: HTMLAttributes['class']
  variant?: 'filled' | 'default' | 'destructive'
  indicator?: 'success' | 'warning' | 'error' | 'custom'
  closable?: boolean
}

const properties = defineProps<ABadgeProperties>()

// end Props

// Emits
const emit = defineEmits<{
  close: [event: MouseEvent]
}>()
// end Emits

// Refs
// end Refs

// Computed
const computedClass = computed(() => {
  return cn(badgeVariants({ variant: properties.variant || 'default' }), properties.class)
})
// end Computed

// Functions
// end Functions

// Async Api functions
// end Async Api functions

// Lifecycle Hooks / Init
// end Lifecycle Hooks / Init
</script>

<template>
  <div :class="computedClass">
    <template v-if="indicator">
      <slot :indicator="indicator" name="indicator">
        <AIndicator :variant="indicator" />
      </slot>
    </template>

    <slot>
      <span class="atabix-badge__label">{{ properties.label }}</span>
    </slot>

    <slot name="closable">
      <template v-if="properties.closable">
        <X :class="cn('size-4', { 'cursor-pointer': properties.closable })" @click="(evt) => emit('close', evt)" />
      </template>
    </slot>
  </div>
</template>

<style lang="scss">
/* stylelint-disable-next-line block-no-empty */

.atabix-badge {
  @apply rounded-md;
  @apply h-5 w-fit;
  @apply flex justify-center items-center;
  @apply text-sm font-semibold;
  @apply px-2 gap-x-2;
}

.atabix-badge--outlined {
  @apply border;
  @apply border-border;
}

.atabix-badge--filled {
  @apply bg-tag-background;
  @apply text-tag-foreground;
  @apply font-semibold;
}

.atabix-badge--destructive {
  @apply bg-background-alert;
  @apply text-foreground-light;
  @apply font-semibold;
}

.atabix-badge__label {
  @apply inline-block;
  @apply text-nowrap;
}
</style>
